import { useEffect, useRef, useState, useCallback, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { getFreshAccessToken, getRefreshToken } from "@/lib/api";
import { parseStaffConnectionKey, useStaffConnectionKey } from "@/lib/staffConnection";
import { NotificationWebSocket, dispatchNotificationEvent, type WsStatus } from "@/lib/notification-ws";
import type { Notification, WsNotificationEvent } from "@/types/notification";
import { playAlertSound, shouldPlayAlert } from "@/lib/alertSound";
import { invalidateOnOrderStageEvent } from "@/lib/orderStageEvents";
import { NotificationContext, type NotificationContextType } from "@/contexts/notification-context";

// ==========================================
// Context Types
// ==========================================

// ==========================================
// Provider
// ==========================================

const MAX_REALTIME_QUEUE = 50;

const QR_ORDER_EVENTS = new Set([
  "qr_order_pending",
  "qr_order_resolved",
  "qr_order_auto_accepted",
  "qr_order_needs_attention",
]);

export function NotificationProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [wsStatus, setWsStatus] = useState<WsStatus>("disconnected");
  const [unreadCount, setUnreadCount] = useState(0);
  const [realtimeQueue, setRealtimeQueue] = useState<Notification[]>([]);
  const wsRef = useRef<NotificationWebSocket | null>(null);

  const decrementUnread = useCallback((n = 1) => {
    setUnreadCount((prev) => Math.max(0, prev - n));
  }, []);

  const clearRealtimeQueue = useCallback(() => {
    setRealtimeQueue([]);
  }, []);

  // Handle incoming WS notification
  const handleWsMessage = useCallback(
    (event: WsNotificationEvent) => {
      // Dispatch to per-type subscribers (e.g. useReservationPromotionWs)
      dispatchNotificationEvent(event);

      // The server moved an order's stage (food ready, handed to rider,
      // completed): refresh the order, table and active-order views.
      invalidateOnOrderStageEvent(queryClient, event);

      // QR orders: a new one, one resolved (accepted, rejected, auto-accepted), or
      // one the auto-accept could not add. The pending list (badge, panel, Accept
      // prompts) refreshes; an auto-accept also changed the table's order.
      if (QR_ORDER_EVENTS.has(event.type)) {
        queryClient.invalidateQueries({ queryKey: ["qr-pending-orders"] });
      }
      if (event.type === "qr_order_auto_accepted") {
        queryClient.invalidateQueries({ queryKey: ["active-orders"], exact: false });
        queryClient.invalidateQueries({ queryKey: ["tables-with-areas"] });
        queryClient.invalidateQueries({ queryKey: ["table-session"] });
      }

      if (event.type === "menu_import_update") {
        const meta = event.metadata as {
          job_id?: string;
          status?: string;
        } | null;
        queryClient.invalidateQueries({ queryKey: ["menu-import-active"] });
        if (meta?.job_id) {
          queryClient.invalidateQueries({ queryKey: ["menu-import-job"] });
        }
      }

      // Silent events (e.g. kitchen KDS kot_ready) refresh subscribers only.
      if (event.silent) {
        return;
      }

      const notification: Notification = {
        notification_id: event.id,
        notification_type: event.type,
        title: event.title,
        body: event.body,
        metadata: event.metadata,
        priority: event.priority,
        channels: event.channels,
        created_at: event.created_at,
        read_at: null,
        dismissed_at: null,
      };

      // Increment unread
      setUnreadCount((prev) => prev + 1);

      // Prepend to realtime queue (cap at MAX_REALTIME_QUEUE)
      setRealtimeQueue((prev) => [notification, ...prev].slice(0, MAX_REALTIME_QUEUE));

      // Invalidate list queries so panel shows new data
      queryClient.invalidateQueries({ queryKey: ["notifications-list"] });

      // The loud alert: "food ready" for this table's waiter, a new KOT for kitchen
      // staff away from the kitchen display (the board plays its own), and anything urgent.
      if (shouldPlayAlert(event)) {
        void playAlertSound();
      }
    },
    [queryClient]
  );

  // Who this tab is signed in as. It changes on sign-in, on picking an outlet or
  // a person, on Switch Outlet/Staff and on sign-out (not on a token refresh).
  const connectionKey = useStaffConnectionKey();

  // Someone else (or nobody) now: nothing of the last person's alerts stays.
  const lastConnectionKey = useRef(connectionKey);
  useEffect(() => {
    if (lastConnectionKey.current === connectionKey) return;
    lastConnectionKey.current = connectionKey;
    setRealtimeQueue([]);
    setUnreadCount(0);
    void queryClient.resetQueries({ queryKey: ["notifications-unread-count"] });
    void queryClient.resetQueries({ queryKey: ["notifications-list"] });
  }, [connectionKey, queryClient]);

  // One socket per signed-in person and outlet: opened on sign-in (no reload
  // needed), replaced on a switch, closed for good on sign-out. Every
  // (re)connect asks for a fresh access token, so token refreshes need nothing here.
  useEffect(() => {
    const connection = parseStaffConnectionKey(connectionKey);
    if (!connection) {
      setWsStatus("disconnected");
      return;
    }

    const ws = new NotificationWebSocket({
      getAccessToken: getFreshAccessToken,
      hasRefreshToken: () => !!getRefreshToken(),
      outletId: connection.outletId,
      profileId: connection.profileId,
      onMessage: handleWsMessage,
      onStatusChange: setWsStatus,
    });
    wsRef.current = ws;
    void ws.connect();

    return () => {
      ws.disconnect();
      if (wsRef.current === ws) wsRef.current = null;
    };
  }, [connectionKey, handleWsMessage]);

  const value: NotificationContextType = {
    wsStatus,
    unreadCount,
    realtimeQueue,
    setUnreadCount,
    decrementUnread,
    clearRealtimeQueue,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}
